/**
 * Universal Size Selector Custom Element
 *
 * Renders a "Womens Sizing" / "Mens Sizing" tabbed view for footwear whose
 * variants carry both sizes in their name ("M 4 / W 5.5"), parsed into
 * $universalSizeMap by the universal-size-selector snippet. Both tabs share the
 * same variant grid, so the selected variant is retained when switching tabs;
 * only the displayed size label changes to the active gender's US size.
 */

declare global {
  interface Window {
    $universalSizeMap: Record<string, {
      SIZE_MENS: string;
      SIZE_WOMENS: string;
    }>;
  }
}

type GenderSizeType = 'WOMENS' | 'MENS'

const GENDER_LABELS: Record<GenderSizeType, string> = {
  WOMENS: 'Womens US',
  MENS: 'Mens US'
}

export class UniversalSizeSelector extends HTMLElement {
  private selectedSizeType: GenderSizeType = 'MENS'
  private sizeTypeOptions: HTMLElement | null = null
  private productId: string = ''
  private isUpdatingStickyATC: boolean = false

  connectedCallback(): void {
    this.init()
    this.attachEventListeners()
    this.updateSizeDisplay()
    setTimeout(() => {
      this.updateStickyATC()
    }, 100)
  }

  private init(): void {
    this.sizeTypeOptions = this.querySelector('[data-size-type-options]')

    const requested = (this.dataset.selectedSize || 'MENS').toUpperCase()
    this.selectedSizeType = requested === 'WOMENS' ? 'WOMENS' : 'MENS'
    this.productId = this.dataset.productId || ''

    this.updateSizeTypeActiveState()
  }

  private attachEventListeners(): void {
    if (this.sizeTypeOptions) {
      this.sizeTypeOptions.addEventListener('click', (e) => {
        const button = (e.target as HTMLElement).closest('[data-size-type]') as HTMLElement
        if (button) {
          const newSizeType = button.dataset.sizeType
          if (newSizeType) {
            this.changeSizeType(newSizeType)
          }
        }
      })
    }

    const variantGridContainer = this.querySelector('[data-variant-grid]')
    if (variantGridContainer) {
      variantGridContainer.addEventListener('click', (e) => {
        const variantButton = (e.target as HTMLElement).closest('[data-variant-id]') as HTMLElement
        if (variantButton && variantButton.dataset.selectOption === 'true') {
          this.updateVariantActiveState(variantButton)
          setTimeout(() => {
            this.updateStickyATC()
          }, 100)
        }
      })
    }

    const form = this.closest('form')
    if (form) {
      const variantSelect = form.querySelector('select[name="id"]')
      if (variantSelect) {
        variantSelect.addEventListener('change', () => {
          setTimeout(() => {
            this.updateStickyATC()
          }, 100)
        })
      }
    }

    this.watchStickyATCLabel()
  }

  private watchStickyATCLabel(): void {
    const stickySizeLabel = document.querySelector('[data-sticky-size-label]') as HTMLElement
    if (!stickySizeLabel) return

    const observer = new MutationObserver(() => {
      if (this.isUpdatingStickyATC) return

      setTimeout(() => {
        this.updateStickyATC()
      }, 50)
    })

    observer.observe(stickySizeLabel, {
      childList: true,
      characterData: true,
      subtree: true
    })
  }

  private changeSizeType(newSizeType: string): void {
    this.selectedSizeType = newSizeType.toUpperCase() === 'MENS' ? 'MENS' : 'WOMENS'

    this.updateSizeTypeActiveState()
    this.updateSizeDisplay()
    this.updateStickyATC()
  }

  private updateSizeTypeActiveState(): void {
    if (!this.sizeTypeOptions) return

    const buttons = this.sizeTypeOptions.querySelectorAll('[data-size-type]')
    buttons.forEach((button) => {
      const htmlButton = button as HTMLElement
      const isActive = htmlButton.dataset.sizeType === this.selectedSizeType

      htmlButton.setAttribute('aria-selected', isActive ? 'true' : 'false')
      htmlButton.setAttribute('tabindex', isActive ? '0' : '-1')
    })
  }

  private updateSizeDisplay(): void {
    const variantGridContainer = this.querySelector('[data-variant-grid]')
    if (!variantGridContainer || !window.$universalSizeMap) return

    const variantButtons = variantGridContainer.querySelectorAll('[data-variant-id]')

    variantButtons.forEach((button) => {
      const htmlButton = button as HTMLElement
      const variantId = htmlButton.dataset.variantId

      if (variantId) {
        const variantData = window.$universalSizeMap[variantId]

        if (variantData) {
          const displaySize = this.selectedSizeType === 'MENS'
            ? variantData.SIZE_MENS
            : variantData.SIZE_WOMENS

          htmlButton.style.setProperty('--display-size', `"${displaySize}"`)
        }
      }
    })
  }

  private updateVariantActiveState(selectedButton: HTMLElement): void {
    const variantGridContainer = this.querySelector('[data-variant-grid]')
    if (!variantGridContainer) return

    const allButtons = variantGridContainer.querySelectorAll('[data-variant-id]')

    allButtons.forEach((button) => {
      button.classList.remove('active')
    })

    selectedButton.classList.add('active')
  }

  private getSizeValue(variantId: string): string {
    const variantData = window.$universalSizeMap?.[variantId]
    if (!variantData) return ''

    const size = this.selectedSizeType === 'MENS' ? variantData.SIZE_MENS : variantData.SIZE_WOMENS
    if (!size) return ''

    return `${GENDER_LABELS[this.selectedSizeType]} ${size}`
  }

  private updateStickyATC(): void {
    if (this.isUpdatingStickyATC) return
    this.isUpdatingStickyATC = true

    const parentLabel = document.querySelector('[data-sticky-size-label]') as HTMLElement

    if (!parentLabel) {
      this.isUpdatingStickyATC = false
      return
    }

    const variantGridContainer = this.querySelector('[data-variant-grid]')
    let activeVariantButton = variantGridContainer?.querySelector('[data-variant-id].active') as HTMLElement

    if (!activeVariantButton) {
      const form = this.closest('form') || document.querySelector(`form[id*="${this.productId}"]`)
      if (form) {
        const variantSelect = form.querySelector('select[name="id"]') as HTMLSelectElement
        if (variantSelect && variantSelect.value) {
          activeVariantButton = variantGridContainer?.querySelector(`[data-variant-id="${variantSelect.value}"]`) as HTMLElement
        }
      }
    }

    if (!activeVariantButton) {
      this.isUpdatingStickyATC = false
      return
    }

    const variantId = activeVariantButton.dataset.variantId
    if (!variantId || !window.$universalSizeMap) {
      this.isUpdatingStickyATC = false
      return
    }

    const sizeValue = this.getSizeValue(variantId)
    if (!sizeValue) {
      this.isUpdatingStickyATC = false
      return
    }

    let optionName = 'Size'
    const existingText = parentLabel.textContent || ''
    const match = existingText.match(/^(Size|Value):/)
    if (match) {
      optionName = match[1]
    }

    let stickySizeText = parentLabel.querySelector('[data-sticky-size-text]') as HTMLElement
    if (!stickySizeText) {
      stickySizeText = document.createElement('span')
      stickySizeText.setAttribute('data-sticky-size-text', '')
      parentLabel.textContent = ''
      parentLabel.appendChild(stickySizeText)
    }
    stickySizeText.textContent = `${optionName}: ${sizeValue}`

    setTimeout(() => {
      this.isUpdatingStickyATC = false
    }, 100)
  }
}

export default UniversalSizeSelector
